// First run. One question, and it is not a settings page.
//
// A profile changes vocabulary, which fields exist, and defaults. It
// never changes the protocol. The app says which one it is on and how to
// change it, from here on, in the corner of the screen — because an
// operator who cannot tell which words they are being given cannot tell
// when the words are wrong.

import { useState } from "react";
import { PROFILE_IDS, profileFor, type ProfileId } from "@wayfind/core";
import { Panel } from "./Bits";

export function Setup({ onPick }: { onPick(profile: ProfileId): void }): JSX.Element {
  return (
    <Panel eyebrow="Before anything else">
      <h1 className="title">What kind of work is this?</h1>
      <p className="subtitle">
        It decides what things are called and which details a stop can carry. Nothing else — the
        protocol underneath is the same one either way, and you can change this later.
      </p>
      <div className="picker picker--two" style={{ marginTop: "0.9rem" }}>
        {PROFILE_IDS.map(id => {
          const profile = profileFor(id);
          return (
            <button key={id} type="button" className="picker__option" onClick={() => onPick(id)}>
              <span className="picker__title">{profile.label}</span>
              <span className="picker__blurb">{profile.summary}</span>
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

/**
 * Which trade this console is speaking, and how to change it.
 *
 * A button rather than a `<select>`, and the difference is not cosmetic:
 * a select keeps every option in the document, so a takeaway at rung 0
 * would have the words "courier fleet" sitting in its interface, unread
 * and unasked for. The other three trades exist here only once somebody
 * asks what else there is.
 */
export function ProfileSwitch({
  profile,
  onPick
}: {
  profile: ProfileId;
  onPick(profile: ProfileId): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="button button--small button--ghost"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      >
        {profileFor(profile).label}
      </button>
      {open ? (
        <div className="switcher" role="dialog" aria-label="What kind of work this is">
          <div className="picker">
            {PROFILE_IDS.map(id => (
              <button
                key={id}
                type="button"
                className="picker__option"
                aria-pressed={id === profile}
                onClick={() => { onPick(id); setOpen(false); }}
              >
                <span className="picker__title">{profileFor(id).label}</span>
                <span className="picker__blurb">{profileFor(id).summary}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}
